import React, { useCallback, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  LabelList,
  TooltipProps
} from "recharts";
import { CustomTooltip } from "./Tooltip";
import { formatNumber } from "@/utils/numbers";
import UserAvatar from "@/components/controls/UserAvatar";
import { Organization, People, World } from "@dreamhub/designsystem/icons";
import { UserData } from "@/entities/users/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { capitalize, isEmpty } from "lodash";

export type ComparisonChartProps = {
  datasets: {
    name: string;
    value: number;
  }[];
  unit?: string;
  hideIndustry?: boolean;
  userData?: UserData;
  showCurrencySign?: boolean;
};

export type tooltipDataType = {
  title: string;
  amount: number;
  unit?: string;
  hideIndustry?: boolean;
  showCurrencySign?: boolean;
};

const YOU_GRADIENT = ["#2FEAA5", "#2AD193"] as const;
const OTHER_GRADIENTS = [
  "var(--Light-grey-700, #667085)",
  "var(--Light-grey-800, #475467)"
] as const;

// Custom bar shape component - defined outside to prevent recreation
const CustomBarShape = ({
  x,
  y,
  width,
  height,
  name,
  isDisabled
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  isDisabled: boolean;
}) => {
  const gradientId = `gradient-${name}`;
  const effectiveHeight = Math.max(height, 2);
  const adjustedY = y + (height - effectiveHeight);

  // Adjust corner radius based on bar dimensions to ensure it looks good for small bars
  const maxRadius = Math.min(8, effectiveHeight / 2, width / 2);
  const cornerRadius = Math.max(2, maxRadius);

  return (
    <path
      d={`
      M ${x + cornerRadius},${adjustedY + effectiveHeight}
      L ${x},${adjustedY + effectiveHeight - cornerRadius}
      Q ${x},${adjustedY + effectiveHeight} ${x + cornerRadius},${adjustedY + effectiveHeight}
      L ${x + width - cornerRadius},${adjustedY + effectiveHeight}
      Q ${x + width},${adjustedY + effectiveHeight} ${x + width},${adjustedY + effectiveHeight - cornerRadius}
      L ${x + width},${adjustedY + cornerRadius}
      Q ${x + width},${adjustedY} ${x + width - cornerRadius},${adjustedY}
      L ${x + cornerRadius},${adjustedY}
      Q ${x},${adjustedY} ${x},${adjustedY + cornerRadius}
      L ${x},${adjustedY + effectiveHeight - cornerRadius}
      Z
    `}
      fill={isDisabled ? "transparent" : `url(#${gradientId})`}
    />
  );
};

// Custom label component - defined outside to prevent recreation
const CustomLabelContent = ({
  x,
  y,
  width,
  value: rawValue,
  name,
  unit,
  isDisabled
}: {
  x: number;
  y: number;
  width: number;
  value: number;
  name: string;
  unit: string;
  isDisabled: boolean;
}) => {
  if (isDisabled) {
    return null;
  }
  let label = "";
  const { value, modifier } = formatNumber(rawValue, 1, false, true);
  if (unit) {
    label = `${rawValue}${unit}`;
  } else {
    label = `${value}${modifier}`;
  }
  return (
    <text
      x={x + width / 2}
      y={y - 10}
      fill="var(--tertiary)"
      textAnchor="middle"
      dominantBaseline="middle"
      className="text-sm pb-6"
      data-test={`${name}-column-label`}
    >
      {label}
    </text>
  );
};

const ComparisonChart: React.FC<ComparisonChartProps> = ({
  datasets,
  unit = "",
  hideIndustry,
  userData,
  showCurrencySign = true
}) => {
  const numGraphBars = useMemo(() => (hideIndustry ? 3 : 4), [hideIndustry]);

  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const { t } = useTranslation();
  const YOU = useMemo(() => t(`comparison.titles.you`), [t]);
  const TEAM = useMemo(() => t(`comparison.titles.team`), [t]);
  const ORG = useMemo(() => t(`comparison.titles.organization`), [t]);
  const INDUSTRY = useMemo(() => t(`comparison.titles.industry`), [t]);

  const gradients = useMemo(
    () => ({
      [YOU]: YOU_GRADIENT,
      [TEAM]: OTHER_GRADIENTS,
      [ORG]: OTHER_GRADIENTS,
      [INDUSTRY]: OTHER_GRADIENTS
    }),
    [YOU, TEAM, ORG, INDUSTRY]
  );

  const isDisabled = useCallback(
    (name: string) => {
      return (name === INDUSTRY && hideIndustry) || false;
    },
    [INDUSTRY, hideIndustry]
  );

  const icons = useMemo(
    () =>
      [
        <UserAvatar
          key="you"
          size="M"
          userData={userData}
        />,
        <IconContainer key="team" size="5" color="secondary">
          <People />
        </IconContainer>,
        <IconContainer key="org" size="5" color="secondary">
          <Organization />
        </IconContainer>,
        <>
          {!hideIndustry && (
            <IconContainer
              key="industry"
              size="5"
              className={
                isDisabled(INDUSTRY) ? "text-disabled" : "text-secondary"
              }
            >
              <World />
            </IconContainer>
          )}
        </>
      ].slice(0, numGraphBars),
    [hideIndustry, numGraphBars, userData, INDUSTRY, isDisabled]
  );

  const handleMouseMove = useCallback((e: any) => {
    if (e && e.activeTooltipIndex !== undefined) {
      setTooltipPos({ x: e.activeCoordinate.x - 40, y: 10 }); // Fixed y position near the top
    }
  }, []);

  // Memoize custom bar renderer to prevent Recharts from accumulating state
  const CustomBar = useCallback(
    (props: any) => {
      const { x, y, width, height, name } = props;
      return (
        <CustomBarShape
          x={x}
          y={y}
          width={width}
          height={height}
          name={name}
          isDisabled={isDisabled(name)}
        />
      );
    },
    [isDisabled]
  );

  const CustomLabel = useCallback(
    (props: any) => {
      const { x, y, width, value, name } = props;
      return (
        <CustomLabelContent
          x={x}
          y={y}
          width={width}
          value={value}
          name={name}
          unit={unit}
          isDisabled={isDisabled(name)}
        />
      );
    },
    [unit, isDisabled]
  );

  const ComparisonTooltip = useCallback(
    ({ active, payload }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        const { value, name } = payload[0].payload;
        const tooltipData: tooltipDataType = {
          title: name || "",
          amount: value || 0,
          unit,
          hideIndustry: isDisabled(name),
          showCurrencySign: showCurrencySign
        };
        return <CustomTooltip data={tooltipData} />;
      }
      return null;
    },
    [unit, isDisabled, showCurrencySign]
  );

  const slicedDatasets = useMemo(
    () => datasets.slice(0, numGraphBars),
    [datasets, numGraphBars]
  );

  const chartMargin = useMemo(() => ({ top: 20 }), []);

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={slicedDatasets}
          margin={chartMargin}
          onMouseMove={handleMouseMove}
        >
          <defs>
            {Object.entries(gradients).map(([name, colors]) => (
              <linearGradient
                id={`gradient-${name}`}
                key={name}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="30%" stopColor={colors[0]} stopOpacity={0.8} />
                <stop offset="95%" stopColor={colors[1]} stopOpacity={0.5} />
              </linearGradient>
            ))}
          </defs>
          <Bar dataKey="value" shape={<CustomBar />} barSize={12}>
            <LabelList dataKey="value" content={<CustomLabel />} />
          </Bar>
          <RechartsTooltip
            content={<ComparisonTooltip />}
            cursor={{ fill: "transparent" }}
            position={tooltipPos}
            wrapperStyle={{ visibility: tooltipPos.x ? "visible" : "hidden" }}
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="justify-around flex w-full mt-3">
        {!isEmpty(datasets) &&
          icons.map((Icon, index) => {
            const name = datasets[index]?.name;
            return (
              <div
                key={index}
                className="flex flex-col justify-end items-center"
              >
                <span className="transform size-5">
                  <Tooltip data-test="kpi-info-tooltip">
                    <TooltipTrigger className="text-h3">{Icon}</TooltipTrigger>
                    <TooltipContent>
                      {isDisabled(name) ? (
                        <Text as="div" variant="smBody" className="w-20">
                          {t("comparison.switchToMilestonesMessage")}
                        </Text>
                      ) : (
                        <Text as="div" variant="labelSecondary">
                          {capitalize(name)}
                        </Text>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </span>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default ComparisonChart;
